// Input sanitization utilities for XSS prevention and data cleaning

/**
 * Sanitizes HTML content to prevent XSS attacks
 * @param html - HTML string to sanitize
 * @returns Sanitized HTML string
 */
const ALLOWED_HTML_TAGS = ['p', 'br', 'strong', 'em', 'u', 'b', 'i', 'ul', 'ol', 'li', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'a', 'img'];

function removeDangerousElements(html: string): string {
  const tags = ['script', 'iframe', 'object', 'embed', 'link', 'meta'];
  return tags.reduce((result, tag) => {
    const paired = new RegExp(`<${tag}\\b[^<]*(?:(?!<\\/${tag}>)<[^<]*)*<\\/${tag}>`, 'gi');
    const unclosed = new RegExp(`<${tag}\\b[^>]*>`, 'gi');
    return result.replace(paired, '').replace(unclosed, '');
  }, html);
}

export function sanitizeHtml(html: string): string {
  if (!html || typeof html !== 'string') {
    return '';
  }

  const withoutDangerousElements = removeDangerousElements(html);

  return withoutDangerousElements.replace(/<\/?([a-zA-Z0-9]+)\b[^>]*>/g, (match, tagName: string) => {
    const tag = tagName.toLowerCase();
    if (!ALLOWED_HTML_TAGS.includes(tag)) {
      return '';
    }
    return match.startsWith('</') ? `</${tag}>` : `<${tag}>`;
  });
}

/**
 * Sanitizes plain text input to prevent XSS and other attacks
 * @param text - Text string to sanitize
 * @returns Sanitized text string
 */
export function sanitizeText(text: string): string {
  if (!text || typeof text !== 'string') {
    return '';
  }

  return text
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '') // Remove script tags and content
    .replace(/<iframe\b[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gi, '') // Remove iframe tags and content
    .replace(/<object\b[^<]*(?:(?!<\/object>)<[^<]*)*<\/object>/gi, '') // Remove object tags and content
    .replace(/<embed\b[^<]*(?:(?!<\/embed>)<[^<]*)*<\/embed>/gi, '') // Remove embed tags and content
    .replace(/javascript:\S*/gi, '') // Remove javascript: URLs and their payload
    .replace(/vbscript:\S*/gi, '') // Remove vbscript: URLs and their payload
    .replace(/data:\S*/gi, '') // Remove data: URLs and their payload
    .replace(/on\w+\s*=\s*[^\s]*/gi, '') // Remove event handlers
    .replace(/[<>]/g, '') // Remove remaining angle brackets
    .trim();
}

/**
 * Sanitizes user input for display in forms and UI
 * @param input - Input string to sanitize
 * @param maxLength - Maximum allowed length
 * @returns Sanitized input string
 */
export function sanitizeInput(input: string, maxLength: number = 1000): string {
  if (!input || typeof input !== 'string') {
    return '';
  }

  return sanitizeText(input)
    .substring(0, maxLength)
    .trim();
}

/**
 * Sanitizes file names to prevent directory traversal and other attacks
 * @param filename - File name to sanitize
 * @returns Sanitized file name
 */
export function sanitizeFilename(filename: string): string {
  if (!filename || typeof filename !== 'string') {
    return '';
  }

  return filename
    .replace(/<[^>]*>/g, '') // Remove tags, including unclosed script tags
    .replace(/[<>:"/\\|?*]/g, '') // Remove dangerous characters
    .replace(/\.\./g, '') // Remove directory traversal attempts
    .replace(/^\.+/, '') // Remove leading dots
    .replace(/\.+$/, '') // Remove trailing dots
    .substring(0, 255) // Limit length
    .trim();
}

/**
 * Sanitizes URLs to prevent malicious redirects
 * @param url - URL to sanitize
 * @returns Sanitized URL or empty string if invalid
 */
export function sanitizeUrl(url: string): string {
  if (!url || typeof url !== 'string') {
    return '';
  }

  try {
    const parsedUrl = new URL(url);

    // Only allow http and https protocols
    if (!['http:', 'https:'].includes(parsedUrl.protocol)) {
      return '';
    }

    // Check for suspicious patterns
    // eslint-disable-next-line no-script-url
    if (parsedUrl.hostname.includes('javascript:') ||
        parsedUrl.hostname.includes('vbscript:') ||
        parsedUrl.hostname.includes('data:')) {
      return '';
    }

    // Return URL without trailing slash if it was added by the browser
    const urlString = parsedUrl.toString();
    return urlString.endsWith('/') && !url.endsWith('/') ? urlString.slice(0, -1) : urlString;
  } catch {
    return '';
  }
}

/**
 * Sanitizes email addresses
 * @param email - Email address to sanitize
 * @returns Sanitized email address
 */
export function sanitizeEmail(email: string): string {
  if (!email || typeof email !== 'string') {
    return '';
  }

  return email
    .toLowerCase()
    .trim()
    .replace(/<[^>]*>/g, '') // Remove tags, including unclosed script tags
    .replace(/[<>]/g, '') // Remove leftover angle brackets
    .substring(0, 254); // Email length limit
}

/**
 * Sanitizes phone numbers
 * @param phone - Phone number to sanitize
 * @returns Sanitized phone number
 */
export function sanitizePhone(phone: string): string {
  if (!phone || typeof phone !== 'string') {
    return '';
  }

  return phone
    .replace(/[^\d+\-().\s]/g, '') // Keep only digits, +, -, (), ., and spaces
    .replace(/\s+/g, ' ') // Normalize spaces
    .trim()
    .substring(0, 20); // Phone number length limit
}

/**
 * Sanitizes JSON data to prevent prototype pollution
 * @param data - Data to sanitize
 * @returns Sanitized data
 */
export function sanitizeJson(data: any): any {
  if (data === null || data === undefined) {
    return data;
  }

  if (typeof data === 'string') {
    try {
      const parsed = JSON.parse(data);
      return sanitizeJson(parsed);
    } catch {
      return sanitizeText(data);
    }
  }

  if (Array.isArray(data)) {
    return data.map(item => sanitizeJson(item));
  }

  if (typeof data === 'object') {
    const sanitized: any = {};
    for (const [key, value] of Object.entries(data)) {
      // Prevent prototype pollution
      if (key === '__proto__' || key === 'constructor' || key === 'prototype') {
        continue;
      }
      sanitized[key] = sanitizeJson(value);
    }
    return sanitized;
  }

  if (typeof data === 'string') {
    return sanitizeText(data);
  }

  return data;
}

/**
 * Validates and sanitizes file uploads
 * @param file - File object to validate
 * @param allowedTypes - Array of allowed MIME types
 * @param maxSize - Maximum file size in bytes
 * @returns Validation result
 */
export function validateFile(file: File, allowedTypes: string[] = [], maxSize: number = 10 * 1024 * 1024): {
  isValid: boolean;
  error?: string;
  sanitizedFile?: File;
} {
  if (!file) {
    return { isValid: false, error: 'No file provided' };
  }

  // Check file size
  if (file.size > maxSize) {
    return { isValid: false, error: `File size exceeds ${maxSize / (1024 * 1024)}MB limit` };
  }

  // Check file type
  if (allowedTypes.length > 0 && !allowedTypes.includes(file.type)) {
    return { isValid: false, error: `File type ${file.type} is not allowed` };
  }

  // Sanitize filename
  const sanitizedName = sanitizeFilename(file.name);
  if (!sanitizedName) {
    return { isValid: false, error: 'Invalid file name' };
  }

  // Create sanitized file
  const sanitizedFile = new File([file], sanitizedName, { type: file.type });

  return { isValid: true, sanitizedFile };
}

/**
 * Escapes HTML entities for safe display
 * @param text - Text to escape
 * @returns Escaped text
 */
export function escapeHtml(text: string): string {
  if (!text || typeof text !== 'string') {
    return '';
  }

  const htmlEscapes: { [key: string]: string } = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;',
  };

  return text.replace(/[&<>"']/g, (match) => htmlEscapes[match]);
}

/**
 * Validates input length
 * @param input - Input to validate
 * @param minLength - Minimum length
 * @param maxLength - Maximum length
 * @returns Validation result
 */
export function validateLength(input: string, minLength: number = 0, maxLength: number = 1000): {
  isValid: boolean;
  error?: string;
} {
  if (input === null || input === undefined) {
    return { isValid: false, error: 'Input is required' };
  }

  if (typeof input !== 'string') {
    return { isValid: false, error: 'Input must be a string' };
  }

  if (input.length < minLength) {
    return { isValid: false, error: `Input must be at least ${minLength} characters long` };
  }

  if (input.length > maxLength) {
    return { isValid: false, error: `Input must be no more than ${maxLength} characters long` };
  }

  return { isValid: true };
}

/**
 * Checks if a string contains potentially dangerous content
 * @param input - Input string to check
 * @returns True if dangerous content is detected
 */
export function containsDangerousContent(input: string): boolean {
  if (!input || typeof input !== 'string') {
    return false;
  }

  const dangerousPatterns = [
    /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi,
    /<iframe\b[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gi,
    /<object\b[^<]*(?:(?!<\/object>)<[^<]*)*<\/object>/gi,
    /<embed\b[^<]*(?:(?!<\/embed>)<[^<]*)*<\/embed>/gi,
    /<link\b[^<]*(?:(?!<\/link>)<[^<]*)*<\/link>/gi,
    /<meta\b[^<]*(?:(?!<\/meta>)<[^<]*)*<\/meta>/gi,
    /on\w+\s*=/gi,
    /javascript:/gi,
    /vbscript:/gi,
    /data:/gi,
    /eval\s*\(/gi,
    /expression\s*\(/gi,
  ];

  return dangerousPatterns.some(pattern => pattern.test(input));
}
